/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth, db } from "@/firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

type FirestoreUser = {
  access?: string[];
  isVerified?: boolean;
};

type AuthContextType = {
  user: User | null;
  firestoreUser: FirestoreUser | null;
  isAuthReady: boolean;
  email: string | null;
  isAuthenticated: boolean | undefined;
  logout: () => void;
  isVerified: boolean | undefined;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
    undefined,
  );
  const [firestoreUser, setFirestoreUser] = useState<FirestoreUser | null>(
    null,
  );

  const [isVerified, setIsVerified] = useState<boolean | undefined>(undefined);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const logout = async () => {
    try {
      await auth.signOut();
      setUser(null);
      setEmail(null);
      setFirestoreUser(null);

      setIsAuthenticated(false);
      console.log("User successfully logged out");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        setUser(currentUser);
        setEmail(currentUser?.email || null);

        if (currentUser) {
          setIsAuthenticated(true);
          try {
            const userRef = doc(db, "users", currentUser.uid);
            const userDoc = await getDoc(userRef);

            if (userDoc.exists()) {
              const userData = userDoc.data();
              setFirestoreUser(userData);
              setIsVerified(userData.isVerified || false);
            } else {
              setFirestoreUser(null);
              setIsVerified(false);
            }
          } catch (error) {
            console.error("Error fetching Firestore user:", error);
            setFirestoreUser(null);
            setIsVerified(false);
          }
        } else {
          setIsAuthenticated(false);
          setIsVerified(false);
          setFirestoreUser(null);
        }

        setIsAuthReady(true); // <-- самое важное!
      },
      (error) => {
        console.error("Error in onAuthStateChanged:", error);
        setIsAuthenticated(false);
        setIsVerified(false);
        setFirestoreUser(null);
        setIsAuthReady(true);
      },
    );

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        firestoreUser,
        email,
        isAuthenticated,
        isVerified,
        logout,
        isAuthReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
